import type { ReactiveController, ReactiveControllerHost } from 'lit';
import { History } from '@o2d/history';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import type { WhiteboardBus } from '../bus/whiteboard-bus.js';
import type { UiAction } from '../bus/ui-action.events.js';
import { objectId, type ObjectId } from '../bus/branded-types.js';
import type { BoardState } from '../state/board-state.js';
import {
    UpdatePropertyCommand,
    type MutableObjectKey,
} from '../command/update-property.command.js';
import { isCheckbox } from '../../features/checkbox/checkbox.types.js';
import { isPerson } from '../../features/person/person.types.js';

type PlainEditField = 'label' | 'name';

/**
 * Lit ReactiveController owning the plain-text inline editor surface for
 * Checkbox-Label (`field='label'`) and Person-Name (`field='name'`).
 *
 * FC17: separate `edit/enter-plain` / `edit/commit-plain` / `edit/cancel-plain`
 * discriminators isolate this controller from StickyEditorController, which
 * means `edit/enter-sticky` is intentionally NOT handled here.
 *
 * The Person-Field-Diskriminator maps the UiAction `field='name'` to the
 * underlying `text` property on PersonObject; `field='label'` maps to the
 * `label` property on CheckboxObject.
 */
export class PlainEditController implements ReactiveController {
    readonly #host: ReactiveControllerHost & HTMLElement;
    readonly #bus: WhiteboardBus;
    readonly #state: BoardState;
    readonly #history: History<BoardState>;
    readonly #disconnect$ = new Subject<void>();

    #editor: HTMLDivElement | null = null;
    #editingId: ObjectId | null = null;
    #editingField: PlainEditField | null = null;
    #readonly = false;

    constructor(
        host: ReactiveControllerHost & HTMLElement,
        bus: WhiteboardBus,
        state: BoardState,
        history: History<BoardState>,
    ) {
        this.#host = host;
        this.#bus = bus;
        this.#state = state;
        this.#history = history;
    }

    hostConnected(): void {
        this.#bus.uiActions$
            .pipe(takeUntil(this.#disconnect$), takeUntil(this.#bus.destroy$))
            .subscribe({
                next: (action) => this.#handle(action),
                error: () => {
                    if (import.meta.env?.DEV) {
                        console.warn('[PlainEditController] bus error swallowed');
                    }
                },
            });
    }

    hostDisconnected(): void {
        this.#disconnect$.next();
        this.#disconnect$.complete();
        this.#removeEditorDom();
        this.#editingId = null;
        this.#editingField = null;
    }

    #handle(action: UiAction): void {
        switch (action.type) {
            case 'edit/enter-plain':
                this.#openEditor(action.id, action.field);
                return;
            case 'edit/commit-plain':
                this.#commit(action.id, action.field, action.value);
                return;
            case 'edit/cancel-plain':
                this.#cancel();
                return;
            default:
                return;
        }
    }

    /**
     * TR7 defense-in-depth: when the host is in readonly mode, the editor
     * must never open. The primary guard lives in EditModeController, but a
     * direct UiAction emit could otherwise still trigger this surface.
     * If invoked while editing, the open editor is closed without committing.
     */
    setReadonly(value: boolean): void {
        this.#readonly = value;
        if (value && this.#editingId !== null) {
            this.#closeEditor();
        }
    }

    #openEditor(id: ObjectId, field: PlainEditField): void {
        if (this.#readonly) return;
        const obj = this.#state.getObject(id);
        // FC10: silently reject unknown / mismatched ids.
        if (!obj) return;
        const initialValue = this.#readField(obj, field);
        if (initialValue === null) return;

        this.#removeEditorDom();

        const editor = document.createElement('div');
        editor.className = 'plain-editor';
        editor.contentEditable = 'plaintext-only';
        editor.spellcheck = false;
        editor.textContent = initialValue;

        const container = this.#host.querySelector<HTMLElement>('.board-root') ?? this.#host;
        container.appendChild(editor);

        this.#editor = editor;
        this.#editingId = id;
        this.#editingField = field;

        editor.addEventListener('blur', () => this.#dispatchCommit());
        editor.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                this.#dispatchCancel();
            } else if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                this.#dispatchCommit();
            }
        });
    }

    #commit(id: ObjectId, field: PlainEditField, value: string): void {
        const target = this.#state.getObject(id);
        if (!target) {
            this.#closeEditor();
            return;
        }
        const propertyKey = field === 'label' ? 'label' : 'text';
        const cmd = new UpdatePropertyCommand(
            id,
            propertyKey as MutableObjectKey,
            value,
        );
        this.#history.push(cmd, this.#state);
        this.#closeEditor();
    }

    #cancel(): void {
        this.#closeEditor();
    }

    #dispatchCommit(): void {
        const id = this.#editingId;
        const field = this.#editingField;
        const editor = this.#editor;
        if (!id || !field || !editor) return;
        const value = editor.textContent ?? '';
        this.#bus.uiActions$.next({
            type: 'edit/commit-plain',
            id: objectId(id),
            field,
            value,
        });
    }

    #dispatchCancel(): void {
        const id = this.#editingId;
        if (!id) return;
        this.#bus.uiActions$.next({ type: 'edit/cancel-plain', id: objectId(id) });
    }

    #closeEditor(): void {
        this.#removeEditorDom();
        this.#editingId = null;
        this.#editingField = null;
    }

    #removeEditorDom(): void {
        const editor = this.#editor;
        if (!editor) return;
        this.#editor = null;
        if (editor.parentNode) editor.remove();
    }

    /**
     * Read the field value off a board object using the Person-Field-Diskriminator
     * mapping: `name` → `text` (Person), `label` → `label` (Checkbox).
     */
    #readField(
        obj: ReturnType<BoardState['getObject']>,
        field: PlainEditField,
    ): string | null {
        if (!obj) return null;
        if (field === 'label' && isCheckbox(obj)) return obj.label ?? '';
        if (field === 'name' && isPerson(obj)) return obj.text;
        return null;
    }
}
